<script setup lang="ts">
import { h, useSlots, computed, cloneVNode, type VNode } from 'vue'
import type { ButtonGroupVariants } from '@/components/shadcn/button-group-variants'
import { ButtonGroup, ButtonGroupSeparator } from '@/components/shadcn'

// -------------------------
// props 및 기본값 선언
const props = withDefaults(
  defineProps<{
    orientation?: ButtonGroupVariants['orientation']  // 수평/수직
    showButtonSeparators?: boolean         // 버튼 사이 separator 표시 여부 (기본값: true)
  }>(),
  {
    orientation: 'horizontal',
    showButtonSeparators: true
  }
)

// useSlots로 하위 슬롯 노드 접근
const slots = useSlots()

// 버튼 사이 separator 표시 여부
const shouldShowButtonSeparators = computed(() => props.showButtonSeparators)

// 버튼의 styletype이 outline인지 확인
const isButtonOutline = (child: any): boolean => {
  if (!child || typeof child !== 'object') return false
  
  // props에서 styletype 확인
  const styletype = child.props?.styletype || child.props?.['styletype']
  return styletype === 'outline'
}

// slot으로 받은 버튼 리스트에 버튼 사이마다 separator를 자동 삽입
// outline 버튼의 양옆에는 구분선을 넣지 않음 (outline은 자체 경계가 있어 구분선이 중복됨)
// outline 버튼이 양옆에 구분선이 없을 때는 왼쪽 border를 복원해야 함
const slotWithSeparators = computed(() => {
  const children = slots.default?.() || []
  if (!shouldShowButtonSeparators.value) return children
  
  const result: VNode[] = []
  children.forEach((child: VNode, idx: number) => {
    if (idx > 0) {
      // 이전 버튼과 현재 버튼 확인
      const prevChild = children[idx - 1]
      const isPrevOutline = isButtonOutline(prevChild)
      const isCurrentOutline = isButtonOutline(child)
      
      // outline 버튼이 포함되지 않을 때만 구분선 추가
      // (outline 버튼은 자체 경계가 있어 양옆에 구분선이 필요 없음)
      if (!(isPrevOutline || isCurrentOutline)) {
        result.push(h(ButtonGroupSeparator, {
          orientation: props.orientation === 'vertical' ? 'horizontal' : 'vertical'
        }))
      }
    }
    
    // outline 버튼이 양옆에 구분선이 없을 때 왼쪽 border 복원
    let childToAdd = child
    if (isButtonOutline(child)) {
      const prevChild = idx > 0 ? children[idx - 1] : null
      const isPrevOutline = prevChild ? isButtonOutline(prevChild) : false
      
      // 이전 버튼이 outline이 아니고 (구분선이 없음), 현재 버튼이 첫 번째가 아니면 왼쪽 border 복원
      if (!isPrevOutline && idx > 0) {
        // VNode를 복제하면서 class 추가
        const existingClass = child.props?.class || child.props?.['class'] || ''
        let newClass: string | Array<string | object> | object
        
        if (typeof existingClass === 'string') {
          // 문자열인 경우: 공백으로 구분하여 추가
          newClass = `${existingClass} !border-l`
        } else if (Array.isArray(existingClass)) {
          // 배열인 경우: 새 클래스를 배열에 추가
          newClass = [...existingClass, '!border-l']
        } else if (existingClass && typeof existingClass === 'object') {
          // 객체인 경우: 객체에 border-l 클래스 추가
          newClass = { ...existingClass, '!border-l': true }
        } else {
          // 기타 경우: 배열로 변환
          newClass = [existingClass, '!border-l'].filter(Boolean)
        }
        
        // cloneVNode를 사용하여 VNode를 복제하고 props 수정
        childToAdd = cloneVNode(child, {
          ...child.props,
          class: newClass
        })
      }
    }
    
    result.push(childToAdd)
  })
  return result
})
</script>

<template>
  <!-- 바깥 ButtonGroup: 버튼 모음 내부 그룹을 수직/수평으로 감쌈 -->
  <ButtonGroup :orientation="props.orientation ?? 'horizontal'">
    <!-- 내부 ButtonGroup: 버튼들 렌더 + 필요시 버튼 사이 separator 자동 삽입 -->
    <ButtonGroup :orientation="props.orientation ?? 'horizontal'">
      <template v-if="shouldShowButtonSeparators">
        <template v-for="(vnode, _idx) in slotWithSeparators" :key="_idx">
          <component :is="vnode" />
        </template>
      </template>
      <slot v-else />
    </ButtonGroup>
  </ButtonGroup>
</template>


